import axios from 'axios';
import { API_BASE } from './api';

/**
 * Fire-and-forget event logger for first-time achievements.
 * Silently swallows all errors — never blocks the caller.
 */
export const logEvent = async (eventKey) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return;
    await axios.post(
      `${API_BASE}/user/log-event`,
      { event: eventKey },
      { headers: { Authorization: `Bearer ${token}` } }
    );
  } catch {
    // intentionally silent
  }
};

/**
 * Fetches ALL pages from a paginated backend endpoint.
 *
 * @param {string} baseUrl      - URL without query params (e.g. `${API_BASE}/data/get-games`)
 * @param {string} dataKey      - Key in the JSON response that holds the items array (e.g. 'games', 'users')
 * @param {object} params       - Extra query params appended to every request (e.g. { sport: 'NBA' })
 * @param {object} axiosConfig  - Optional axios config (e.g. { headers: { Authorization: '...' } })
 * @param {number} perPage      - Items per page to request (default 50)
 * @returns {Promise<Array>}    - Flat array of all items across all pages
 */
export const fetchAllPages = async (baseUrl, dataKey, params = {}, axiosConfig = {}, perPage = 50) => {
  const makeUrl = (page) => {
    const qs = new URLSearchParams({ ...params, page, per_page: perPage });
    return `${baseUrl}?${qs}`;
  };

  const firstRes = await axios.get(makeUrl(1), axiosConfig);
  const { [dataKey]: firstItems, pages } = firstRes.data;

  if (pages <= 1) return firstItems;

  const remaining = await Promise.all(
    Array.from({ length: pages - 1 }, (_, i) => axios.get(makeUrl(i + 2), axiosConfig))
  );

  return [...firstItems, ...remaining.flatMap(r => r.data[dataKey])];
};
